import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Star, PhoneCall } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Reveal from "../components/Reveal";
import AnimatedCounter from "../components/AnimatedCounter";
import Seo from "../components/Seo";
import {
  hero, partners, services, process, stats, whyChoose, testimonials, caseStudies, company, networks,
} from "../data/mock";

const Home = () => {
  return (
    <div className="overflow-hidden">
      <Seo />
      {/* ===== HERO ===== */}
      <section className="relative bg-white">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="blob h-72 w-72 bg-blue-400 -top-10 left-1/4" />
        <div className="blob h-72 w-72 bg-sky-300 top-40 right-0" />
        <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-100 font-semibold px-3 py-1.5 rounded-full">
                <Star className="h-3.5 w-3.5 mr-1.5 fill-blue-600 text-blue-600" />
                {hero.badge}
              </Badge>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl xl:text-6xl font-extrabold text-slate-900 leading-[1.05]">
                {hero.titleTop} <span className="text-gradient">{hero.titleAccent}</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">{hero.subtitle}</p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                  <Button size="lg" className="brand-gradient text-white font-bold text-base shadow-xl shadow-blue-600/25 hover:shadow-blue-600/40 transition-shadow group w-full sm:w-auto">
                    {hero.primaryCta}
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="font-bold text-base border-slate-300 hover:border-blue-600 hover:text-blue-600 w-full sm:w-auto">
                    {hero.secondaryCta}
                  </Button>
                </Link>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                {["No long-term contracts", "Free strategy call", "US-based team"].map((t) => (
                  <span key={t} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-600" /> {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={200} className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200">
              <img src={hero.image} alt="Digital marketing team collaborating" className="w-full h-[420px] lg:h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
            </div>
            <div className="absolute -left-4 top-10 animate-float">
              <div className="glass rounded-2xl shadow-xl border border-white/60 px-5 py-4">
                <p className="font-display text-3xl font-extrabold text-blue-600">{hero.floatStat1.value}</p>
                <p className="text-xs font-semibold text-slate-600 mt-1">{hero.floatStat1.label}</p>
              </div>
            </div>
            <div className="absolute -right-4 bottom-10 animate-float" style={{ animationDelay: "1.5s" }}>
              <div className="glass rounded-2xl shadow-xl border border-white/60 px-5 py-4">
                <p className="font-display text-3xl font-extrabold text-sky-500">{hero.floatStat2.value}</p>
                <p className="text-xs font-semibold text-slate-600 mt-1">{hero.floatStat2.label}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== PARTNER MARQUEE ===== */}
      <section className="border-y border-slate-200 bg-slate-50 py-8">
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-6">
            Certified partners &amp; platforms we master
          </p>
          <div className="relative overflow-hidden">
            <div className="flex w-max animate-marquee gap-16">
              {[...partners, ...partners].map((p, i) => (
                <span key={i} className="font-display text-xl font-bold text-slate-400 whitespace-nowrap">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">What we do</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">
              A digital solution for every industry
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Instead of juggling multiple agencies, our unified team delivers comprehensive marketing services that work together and compound results.
            </p>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 70}>
                <Link to="/services" className="group block h-full rounded-2xl border border-slate-200 bg-white overflow-hidden card-lift">
                  <div className="relative h-44 overflow-hidden">
                    <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                    <div className="absolute top-4 left-4 flex h-11 w-11 items-center justify-center rounded-xl brand-gradient shadow-lg">
                      <s.icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="absolute bottom-3 left-4 font-display text-xl font-bold text-white">{s.title}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600 leading-relaxed">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
                      View more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">How it works</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Our simple 4-step process</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <Reveal key={p.step} delay={i * 90}>
                <div className="relative h-full rounded-2xl bg-white border border-slate-200 p-7 card-lift">
                  <span className="font-display text-5xl font-extrabold text-slate-100 absolute top-4 right-5 select-none">{p.step}</span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <p.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="relative section-pad navy-gradient overflow-hidden">
        <div className="blob h-72 w-72 bg-blue-500 top-0 right-1/4" />
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white">The numbers you need to know</h2>
            <p className="mt-3 text-blue-100/70">Real results from a team obsessed with your growth.</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="text-center">
                <AnimatedCounter end={s.value} suffix={s.suffix} className="font-display text-5xl md:text-6xl font-extrabold text-white" />
                <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-blue-200/80">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== NETWORKS TEASER ===== */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Performance marketing</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Grow through our partner networks</h2>
              <p className="mt-4 text-lg text-slate-600">Pay only for results with our affiliate and pay-per-call networks — connecting you with vetted partners who deliver real sales and ready-to-buy calls.</p>
            </div>
            <Link to="/networks">
              <Button variant="outline" className="font-bold border-slate-300 hover:border-blue-600 hover:text-blue-600 group whitespace-nowrap">
                Explore networks
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {networks.map((n, i) => (
              <Reveal key={n.slug} delay={i * 100}>
                <Link to="/networks" className="group block h-full rounded-2xl border border-slate-200 bg-white overflow-hidden card-lift">
                  <div className="relative h-48 overflow-hidden">
                    <img src={n.image} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                    <div className="absolute top-4 left-4 flex h-11 w-11 items-center justify-center rounded-xl brand-gradient shadow-lg">
                      <n.icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="absolute bottom-3 left-4 right-4 font-display text-xl font-bold text-white">{n.title}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600 leading-relaxed">{n.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
                      Learn more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE ===== */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-3 gap-8">
          {whyChoose.map((w, i) => (
            <Reveal key={w.title} delay={i * 90}>
              <div className="h-full rounded-2xl border border-slate-200 p-8 card-lift bg-gradient-to-b from-white to-slate-50/50">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl brand-gradient shadow-lg shadow-blue-600/25">
                  <w.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-slate-900">{w.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== CASE STUDY TEASER ===== */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Proof, not promises</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Results that speak for themselves</h2>
            </div>
            <Link to="/case-studies">
              <Button variant="outline" className="font-bold border-slate-300 hover:border-blue-600 hover:text-blue-600 group">
                View all case studies
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {caseStudies.slice(0, 2).map((c, i) => (
              <Reveal key={c.client} delay={i * 100}>
                <div className="group rounded-2xl overflow-hidden bg-white border border-slate-200 card-lift">
                  <div className="relative h-52 overflow-hidden">
                    <img src={c.image} alt={c.client} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <Badge className="absolute top-4 left-4 bg-white/90 text-blue-700 hover:bg-white font-semibold">{c.industry}</Badge>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-bold text-slate-900">{c.client}</h3>
                    <p className="mt-2 text-slate-600">{c.summary}</p>
                    <div className="mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-slate-100">
                      {c.metrics.map((m) => (
                        <div key={m.label}>
                          <p className="font-display text-2xl font-extrabold text-blue-600">{m.value}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Testimonials</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Loved by businesses like yours</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 90}>
                <div className="h-full rounded-2xl border border-slate-200 p-8 bg-slate-50/50 card-lift">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, k) => (
                      <Star key={k} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="mt-5 text-slate-700 leading-relaxed italic">“{t.quote}”</p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full brand-gradient text-white font-bold text-sm">
                      {t.initials}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{t.name}</p>
                      <p className="text-sm text-slate-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CALL CTA ===== */}
      <section className="pb-24 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 px-8 py-10 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="hidden sm:flex h-16 w-16 items-center justify-center rounded-2xl brand-gradient shadow-lg shadow-blue-600/25">
                  <PhoneCall className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold text-slate-900">Prefer to talk it through?</h3>
                  <p className="mt-1 text-slate-600">Call us now and speak with a real marketing strategist.</p>
                </div>
              </div>
              <a href={company.phoneHref}>
                <Button size="lg" className="brand-gradient text-white font-bold text-lg px-8">
                  {company.phone}
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
